import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ShieldCheck, Award, Wrench, Leaf } from "lucide-react";
import Reveal from "../Reveal";
import { STATS } from "../../lib/site";

const BADGES = [
  { icon: ShieldCheck, title: "Tier-1 panels", text: "Only bankable, top-tier modules" },
  { icon: Award, title: "25-year warranty", text: "Performance you can count on" },
  { icon: Wrench, title: "Turnkey install", text: "Structure, wiring & paperwork" },
  { icon: Leaf, title: "Full subsidy help", text: "PM Surya Ghar handled for you" },
];

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(value);
  const num = parseFloat(value.replace(/[^0-9.]/g, ""));
  const suffix = value.replace(/[0-9.,]/g, "");
  useEffect(() => {
    if (!inView || isNaN(num)) return;
    let start = 0;
    const dur = 1400, t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = num * eased;
      setDisplay((num >= 100 ? Math.round(cur) : cur.toFixed(1)) + suffix);
      if (p < 1) requestAnimationFrame(tick);
      else setDisplay(value);
    };
    requestAnimationFrame(tick);
  }, [inView, num, suffix, value]);
  return <span ref={ref} className="font-heading text-4xl font-extrabold text-forest md:text-6xl">{display}</span>;
}

export default function Trust() {
  return (
    <section data-testid="trust" className="border-y border-line bg-cream py-24 md:py-28">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid gap-8 border-b border-line pb-16 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <Counter value={s.value} />
              <p className="mt-2 text-sm text-moss">{s.label}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BADGES.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08}>
              <motion.div whileHover={{ y: -6 }} className="h-full rounded-3xl border border-line bg-white/50 p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-cream">
                  <b.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-forest">{b.title}</h3>
                <p className="mt-2 text-sm text-moss">{b.text}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
